import React, { useEffect, useState } from "react";
import { toast as newToast } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import PaymentProcessing from "./paymentProcessing";
import { useAppDispatch } from "@/hooks/useReduxHooks";
import { setShowPaymentProcessing ,setShowPaymentSuccess} from "@/store/slices/global.Slice";
import Strings from "@/constants/String";

/*
    Common Razorpay Component for Vendor Payments
    Works for:
      - Onboarding Fee
      - Direct Booking Subscription
      - Website Subscription
*/

type VendorPaymentProps = {
  apiCreateOrder: any;      // thunk function for creating order
  apiVerifyPayment: any;    // thunk function for verifying payment
  purchaseType: string;     // e.g. "onboarding" | "directBooking" | "customWebsite"
  metadata?: any;           // { venueId?, promoCode?, planId? }
  onSuccess: (data: any) => void;
  onClose: () => void;
  backDropcloseHandler?:()=>void
};

const VendorPayment = ({
  backDropcloseHandler,
  apiCreateOrder,
  apiVerifyPayment,
  purchaseType,
  metadata = {},
  onSuccess,
  onClose,
}: VendorPaymentProps) => {
  const dispatch = useAppDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [startPayment, setStartPayment] = useState(true);

  // ------------------------------
  // 🔥 SUCCESS HANDLER
  // ------------------------------
  const handleSuccess = (response: any, orderDetails: any) => {
    const { razorpay_order_id, razorpay_payment_id, razorpay_signature } = response;

    const verifyPayload = {
      razorpay_order_id,
      razorpay_payment_id,
      razorpay_signature,
      ...metadata, // venueId / promoCode / planId anything
    };

    newToast.success(
      `Payment successful. Verifying...\nTxn: ${razorpay_payment_id}`,
      { closeButton: true }
    );
    dispatch(setShowPaymentProcessing(true))
    setIsProcessing(true);
    setStartPayment(false);

    const verifyCall = () => {
      dispatch(
        apiVerifyPayment({
          payload: verifyPayload,
          callback: (res: any) => {
            if (res?.payload) {
              setIsProcessing(false);
              dispatch(setShowPaymentProcessing(false))
              dispatch(setShowPaymentSuccess(true))
              onSuccess(res.payload);
            } else {
              console.log("Retrying verification...");
              setTimeout(verifyCall, 2000);
            }
          },
        })
      );
    };

    setTimeout(verifyCall, 1500);
  };

  // ------------------------------
  // 🔥 ERROR HANDLER
  // ------------------------------
  const handlePaymentError = (err: any) => {
    console.error("Payment failed:", err);
    newToast.error(err?.error?.description || "Payment failed", {
      closeButton: true,
    });

    onClose();
  };

  // ------------------------------
  // 🔥 INITIATE RAZORPAY PAYMENT
  // ------------------------------
  const startRazorpayFlow = () => {
    try {
        dispatch(
            apiCreateOrder({
              payload: metadata,
              callback: (res: any) => {
                if(res?.error){
                  onClose();
                  return;
                }
                const order = res?.payload;
                if (!order) {
                  newToast.error("Failed to initiate payment");
                  onClose();
                  return;
                }
      
                const {
                  key,
                  id: order_id,
                  amount,
                  currency,
                  prefill,
                  method,
                } = order;
      
                const PAYMENT_DESCRIPTIONS: any = {
                  onboarding: "Venue Onboarding Fee",
                  directBooking: "Direct Booking Link Subscription",
                  website: "Custom Website Subscription",
                  venueRecomendation:"Venue recomendation payment"
                };
      
                const options: any = {
                  key,
                  order_id,
                  amount,
                  currency,
                  name: "BookYourZone",
                  description: PAYMENT_DESCRIPTIONS[purchaseType],
                  image: Strings.logoUrl,
                  prefill,
                  method: method || { upi: true },
                  theme: { color: "#4B3DF8" },
                  timeout:Strings.paymentExpriryTime,
                  handler: (response: any) => handleSuccess(response, order),
                  modal: {
                    ondismiss: () => {
                      newToast.error("Payment cancelled by user");
                      onClose();
                    },
                  },
                };
                {typeof backDropcloseHandler ==="function" && backDropcloseHandler()}
                const rzp = new (window as any).Razorpay(options);
                rzp.on("payment.failed", handlePaymentError);
                rzp.open();
              },
            })
          );
    } catch (error) {
        setStartPayment(false);

    }
  
  };

  useEffect(() => {
    startRazorpayFlow();
  }, []);

  return (
    <>
      {isProcessing && <PaymentProcessing />}
      {startPayment && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50">
          <Loader2 className="animate-spin w-14 h-14 text-flup-violet" />
        </div>
      )}
    </>
  );
};

export default VendorPayment;
